import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Gauge, HelpCircle, ArrowRight } from "lucide-react";
import { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { getVehiclePerformance, RANGE_PRESETS, fmtPct, fmtPeso } from "../../lib/analytics";
import UpgradeCard from "../UpgradeCard";

/**
 * Phase 2J: per-vehicle utilization + recorded financial summary.
 * Every number here is defined server-side (utilization_service.py /
 * finance_service.py) and rendered verbatim, including the honesty note
 * for untracked historical days: this component never invents its own
 * wording for what a number means.
 */
export default function VehiclePerformancePanel({ vehicleId }) {
  const { user } = useAuth();
  const isOwner = (user?.account_type || user?.role) === "business_owner";
  const [range, setRange] = useState("this_month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planError, setPlanError] = useState(null);

  const load = async (r) => {
    setLoading(true);
    setPlanError(null);
    try {
      const d = await getVehiclePerformance(vehicleId, { range: r });
      setData(d);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error_code === "plan_upgrade_required") {
        setPlanError(detail);
      } else {
        toast.error(formatApiError(e));
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(range); }, [vehicleId, range]);

  if (planError) {
    return (
      <UpgradeCard
        detail={planError} isOwner={isOwner}
        title="Vehicle performance is not included in this plan"
        features={[
          "Utilization rate: rental days over genuinely available days",
          "Idle vs. maintenance vs. Needs Inspection downtime, broken out",
          "Completed booking value + recorded expenses/income/net",
        ]}
      />
    );
  }

  if (loading) return <div className="text-sm text-slate-500" data-testid="vehicle-performance-loading">Loading performance…</div>;
  if (!data) return null;

  const noRentalDataYet = data.tracked_days === 0 && data.untracked_days === 0 && data.rental_days === 0;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4" data-testid="vehicle-performance-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-lg font-semibold inline-flex items-center gap-2">
          <Gauge className="w-4.5 h-4.5 text-[#1B4332]" /> Performance
        </h2>
        <div className="flex gap-1" data-testid="vehicle-performance-range-picker">
          {RANGE_PRESETS.map((p) => (
            <button key={p.value} onClick={() => setRange(p.value)}
                    data-testid={`vp-range-${p.value}`}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium border ${
                      range === p.value ? "bg-[#1B4332] text-white border-[#1B4332]" : "bg-white border-slate-300 hover:bg-slate-50"}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {data.rental_days === 0 && data.tracked_days === 0 && data.untracked_days === 0 ? (
        <div className="text-sm text-slate-500 py-6 text-center" data-testid="vehicle-performance-empty">
          No utilization data yet: complete rentals to start seeing performance.
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-3xl font-display font-bold text-[#1B4332]" data-testid="vp-utilization-rate">
              {fmtPct(data.utilization_rate)}
            </span>
            <span className="text-sm text-slate-500">utilized</span>
            <span className="group relative">
              <HelpCircle className="w-3.5 h-3.5 text-slate-400" />
              <span className="hidden group-hover:block absolute left-0 top-5 z-10 w-72 rounded-lg bg-slate-900 text-white text-xs p-2.5 shadow-lg">
                Rental days divided by days the vehicle was genuinely available for rental
                (maintenance, Needs Inspection, and other downtime are excluded from the
                denominator, not counted against you).
              </span>
            </span>
          </div>

          {data.note && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="vp-untracked-note">
              {data.note}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Metric label="Rental days" value={data.rental_days}
                    to={`/app/bookings?vehicle=${vehicleId}&status=completed`} testId="vp-rental-days" />
            <Metric label="Idle days" value={data.idle_days} testId="vp-idle-days" />
            <Metric label="Downtime days" value={data.maintenance_days + data.needs_inspection_days}
                    to={`/app/fleet-calendar?vehicle=${vehicleId}`} testId="vp-downtime-days" />
            <Metric label="Untracked days" value={data.untracked_days} muted testId="vp-untracked-days" />
          </div>

          <div className="border-t border-slate-100 pt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Money label="Completed booking value" value={data.completed_booking_value} testId="vp-completed-value"
                   hint="Sum of the price on completed bookings for this vehicle, not the same as cash collected." />
            <Money label="Recorded expenses" value={data.recorded_expenses} testId="vp-recorded-expenses"
                   hint="Finance records logged in Administration plus maintenance costs logged on this vehicle. Log a cost in one place to avoid double-counting." />
            <Money label="Recorded income" value={data.recorded_income} testId="vp-recorded-income"
                   hint="Only income manually recorded in Administration for this vehicle." />
            <Money label="Recorded net" value={data.recorded_net} testId="vp-recorded-net"
                   hint="Recorded income minus recorded expenses. Based only on amounts recorded in ConfirmPH." />
          </div>

          <div className="flex flex-wrap gap-3 pt-1">
            <Link to={`/app/fleet-calendar?vehicle=${vehicleId}`} className="text-xs text-[#1B4332] font-medium hover:underline inline-flex items-center gap-1">
              View on Fleet Calendar <ArrowRight className="w-3 h-3" />
            </Link>
            <Link to="/app/analytics/fleet" className="text-xs text-[#1B4332] font-medium hover:underline inline-flex items-center gap-1">
              Compare across fleet <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ label, value, to, muted, testId }) {
  const body = (
    <div className={`rounded-lg border p-3 ${muted ? "border-slate-100 bg-slate-50 text-slate-500" : "border-slate-200 text-slate-900"}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.15em] font-semibold opacity-70">{label}</div>
      <div className="mt-1 text-xl font-display font-semibold">{value ?? 0}</div>
    </div>
  );
  return to ? <Link to={to} className="hover:opacity-80">{body}</Link> : body;
}

function Money({ label, value, hint, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={testId} title={hint}>
      <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-display font-semibold text-slate-900">{fmtPeso(value)}</div>
    </div>
  );
}
